import { LinkExternal01 } from "@untitledui/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { buildUrl, paths } from "@/api/url";
import { http } from "@/lib/http";
import { selectFluxxUserId, selectSelectedRole } from "@/store/roleSlice";
import { selectTheme } from "@/store/theme";

interface Notification {
  id: string;
  message: string;
  time: string;
  status: string;
  unread?: boolean;
}

const NotificationsDropdown: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { t } = useTranslation();
  const selectedRole = useSelector(selectSelectedRole);
  const selectedFluxxUserId = useSelector(selectFluxxUserId);
  const theme = useSelector(selectTheme);
  const fetchNotifications = async () => {
    try {
      const res = await http.get(
        buildUrl(paths.v1.notifications(selectedRole ?? "", selectedFluxxUserId ?? ""))
      );
      setNotifications(res.data?.data ?? []);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  useEffect(() => {
    if (!selectedFluxxUserId || !selectedRole) return;

    fetchNotifications();
  }, [selectedFluxxUserId, selectedRole]);

  const unreadCount = notifications.filter((n) => n.unread).length;
  const visibleNotifications = notifications.slice(0, 5);

  return (
    <div className="w-full max-w-md bg-white rounded-xl border border-gray-200 shadow-lg flex flex-col">
      {/* HEADER */}
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <span className="text-md font-medium text-gray-950">
          {t("components.navBar.notifications")}
        </span>

        {unreadCount > 0 && (
          <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
            {unreadCount}
          </span>
        )}
      </div>

      {/* LIST (scroll only if needed) */}
      <div className={`overflow-y-auto ${notifications.length > 5 ? "max-h-[280px]" : ""}`}>
        {visibleNotifications.map((item, index) => (
          <div key={item.id}>
            <div className="px-4 py-3 hover:bg-gray-50 cursor-pointer">
              <div className="flex gap-2 items-start">
                {/* Unread indicator */}
                {item.unread && (
                  <span className="mt-2 h-2 w-2 rounded-full bg-orange-500 shrink-0" />
                )}

                <div className="flex-1">
                  {/* Message */}
                  <div className="text-sm text-gray-950">{item.message}</div>

                  {/* Time */}
                  <div className="text-xs text-gray-400 mt-1">{item.time}</div>

                  {/* Status */}
                  <div className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full border border-gray-300 text-gray-700">
                    {item.status}
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            {index !== visibleNotifications.length - 1 && (
              <div className="mx-4 border-t border-gray-200" />
            )}
          </div>
        ))}
      </div>

      {/* FOOTER (Theme-aware) */}
      <div className="border-t border-gray-200 px-4 py-3 flex justify-center">
        <a
          href="https://mastercardfdn.preprod.fluxxlabs.com/"
          target="_blank"
          rel="noopener noreferrer"
          className={`${
            theme === "theme1"
              ? "w-full inline-flex items-center justify-center gap-2 bg-[var(--color-brand)] text-white text-sm font-medium py-2 rounded-lg transition hover:opacity-90"
              : "inline-flex items-center gap-1 text-sm font-medium text-[var(--color-brand)] hover:underline"
          }`}
        >
          {t("components.programMetrics.viewInFluxx")}

          <LinkExternal01
            className={`h-4 w-4 ${theme === "theme1" ? "text-white" : "text-[var(--color-brand)]"}`}
          />
        </a>
      </div>
    </div>
  );
};

export default NotificationsDropdown;
